import React, { useState } from 'react';
import { Modal, Form, InputNumber, Button, Alert, Spin, Typography, Table, Tag, Space, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useReprocessBlock } from '../../hooks/useIndexerManagement';
import { formatWeiToEth } from '../../utils/formatters';

const { Title, Text } = Typography;

interface ReprocessBlockModalProps {
  blockchain: string;
  visible: boolean;
  onClose: () => void;
}

interface Transaction {
  hash: string;
  from: string;
  to: string;
  value: string;
  valueEth?: string; // Optional field for formatted ETH value
  isMonitored: boolean;
  monitoredAddress: string | null;
}

const ReprocessBlockModal: React.FC<ReprocessBlockModalProps> = ({ blockchain, visible, onClose }) => {
  const { t } = useTranslation(['indexer', 'common']);
  const [form] = Form.useForm();
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Mutation for reprocessing a block
  const reprocessBlockMutation = useReprocessBlock();

  // Handle form submission
  const handleSubmit = async (values: { blockNumber: number }) => {
    try {
      setError(null);
      setResult(null);

      // Call the API to reprocess the block
      const response = await reprocessBlockMutation.mutateAsync({
        blockchain,
        blockNumber: values.blockNumber
      });

      // Set the result
      setResult(response);
    } catch (err: any) {
      setError(err.message || 'An error occurred while reprocessing the block');
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.resetFields();
    setResult(null);
    setError(null);
    onClose();
  };

  // Define table columns for transactions
  const columns = [
    {
      title: t('indexer:transactionHash'),
      dataIndex: 'hash',
      key: 'hash',
      render: (hash: string) => (
        <a
          href={`https://${blockchain.toLowerCase().includes('ethereum') ?
            (blockchain.toLowerCase().includes('holesky') ? 'holesky.' : '') + 'etherscan.io' :
            blockchain.toLowerCase() + '.com'}/tx/${hash}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {hash.substring(0, 10)}...{hash.substring(hash.length - 8)}
        </a>
      ),
    },
    {
      title: t('indexer:from'),
      dataIndex: 'from',
      key: 'from',
      render: (from: string) => (
        <Text copyable={{ text: from }}>
          {from.substring(0, 8)}...{from.substring(from.length - 6)}
        </Text>
      ),
    },
    {
      title: t('indexer:to'),
      dataIndex: 'to',
      key: 'to',
      render: (to: string) => (
        to ? (
          <Text copyable={{ text: to }}>
            {to.substring(0, 8)}...{to.substring(to.length - 6)}
          </Text>
        ) : (
          <Text type="secondary">{t('indexer:contractCreation')}</Text>
        )
      ),
    },
    {
      title: t('indexer:value'),
      dataIndex: 'value',
      key: 'value',
      render: (value: string, record: Transaction) => {
        // Use valueEth if available, otherwise format the value from Wei to ETH
        const formattedValue = record.valueEth || formatWeiToEth(value);
        return `${formattedValue} ${blockchain.toLowerCase().includes('ethereum') ? 'ETH' : ''}`;
      },
    },
    {
      title: t('indexer:monitored'),
      dataIndex: 'isMonitored',
      key: 'isMonitored',
      render: (isMonitored: boolean, record: Transaction) => (
        isMonitored ? (
          <Space>
            <Tag color="green">{t('common:yes')}</Tag>
            {record.monitoredAddress && (
              <Text copyable={{ text: record.monitoredAddress }}>
                {record.monitoredAddress.substring(0, 8)}...
              </Text>
            )}
          </Space>
        ) : (
          <Tag color="red">{t('common:no')}</Tag>
        )
      ),
    },
  ];

  return (
    <Modal
      title={t('indexer:reprocessBlock', { blockchain: blockchain.toUpperCase() })}
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="blockNumber"
          label={t('indexer:blockNumber')}
          rules={[{ required: true, message: t('indexer:blockNumberRequired') }]}
        >
          <InputNumber
            min={0}
            style={{ width: '100%' }}
            placeholder={t('indexer:enterBlockNumber')}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={reprocessBlockMutation.isPending}
          >
            {t('indexer:reprocessBlock')}
          </Button>
        </Form.Item>
      </Form>

      {reprocessBlockMutation.isPending && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <Spin />
          <div style={{ marginTop: 10 }}>{t('indexer:processingBlock')}</div>
        </div>
      )}

      {error && (
        <Alert
          message={t('common:error')}
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {result && (
        <div style={{ marginTop: 16 }}>
          <Alert
            message={t('common:success')}
            description={result.message || t('indexer:blockReprocessed')}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Divider />

          {result.data ? (
            <>
              <Title level={5}>{t('indexer:blockDetails')}</Title>
              <div style={{ marginBottom: 16 }}>
                <Space direction="vertical">
                  <Text><strong>{t('indexer:blockNumber')}:</strong> {result.data?.blockNumber}</Text>
                  <Text><strong>{t('indexer:blockHash')}:</strong> {result.data?.blockHash}</Text>
                  <Text><strong>{t('indexer:timestamp')}:</strong> {result.data?.timestamp}</Text>
                  <Text><strong>{t('indexer:transactionCount')}:</strong> {result.data?.transactionCount}</Text>
                </Space>
              </div>

              <Title level={5}>{t('indexer:transactions')}</Title>
              <Table
                dataSource={result.data?.transactions || []}
                columns={columns}
                rowKey="hash"
                pagination={{ pageSize: 5 }}
                size="small"
              />
            </>
          ) : (
            <Alert
              message={t('indexer:noDetailedData')}
              description={t('indexer:blockReprocessedNoDetails')}
              type="info"
              showIcon
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default ReprocessBlockModal;
